import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckSquare,
  Clock,
  AlertCircle,
  Calendar,
  Loader2,
  User,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function TasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [activeTab]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const completed = activeTab === 'completed';
      const response = await axios.get(`${API}/tasks?completed=${completed}`);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (taskId, currentState) => {
    try {
      await axios.put(`${API}/tasks/${taskId}?completed=${!currentState}`);
      toast.success(currentState ? 'Task marked as pending' : 'Task completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tasks/${taskToDelete.id}`);
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = parseISO(dueDate);
    return isPast(due) && !isToday(due);
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return '';
    const due = parseISO(dueDate);
    if (isToday(due)) return 'Today';
    return format(due, 'MMM d, yyyy');
  };

  // Calculate stats
  const allTasks = [...tasks];
  const pendingTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);
  const overdueTasks = tasks.filter(t => !t.completed && isOverdue(t.due_date));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Tasks
          </h1>
          <p className="text-slate-600 mt-1">Manage tasks across all leads</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tasks.length}</p>
                <p className="text-sm text-slate-500">Total Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingTasks.length}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedTasks.length}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={overdueTasks.length > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : ''}`}>
                  {overdueTasks.length}
                </p>
                <p className="text-sm text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasks List */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b border-slate-200">
            <TabsList className="w-full justify-start rounded-none h-auto p-0 bg-transparent">
              <TabsTrigger
                value="pending"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent px-6 py-3"
                data-testid="tab-pending"
              >
                <Clock className="h-4 w-4 mr-2" />
                Pending ({pendingTasks.length})
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent px-6 py-3"
                data-testid="tab-completed"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Completed
              </TabsTrigger>
            </TabsList>
          </div>
          
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12">
                <CheckSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700">
                  {activeTab === 'pending' ? 'All caught up!' : 'No completed tasks'}
                </h3>
                <p className="text-slate-500 mt-1">
                  {activeTab === 'pending' ? 'No pending tasks' : 'Complete some tasks to see them here'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => {
                  const overdue = isOverdue(task.due_date) && !task.completed;
                  
                  return (
                    <div
                      key={task.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                        overdue
                          ? 'bg-red-50 border-red-200'
                          : task.completed
                          ? 'bg-slate-50 border-slate-200'
                          : 'bg-white border-slate-200 hover:border-indigo-200'
                      }`}
                      data-testid={`task-${task.id}`}
                    >
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => handleToggleComplete(task.id, task.completed)}
                        className="mt-1"
                        data-testid={`task-checkbox-${task.id}`}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${task.completed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {task.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          {/* Lead Link */}
                          <button
                            onClick={() => navigate(`/admin/leads/${task.lead_id}`)}
                            className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                            data-testid={`task-lead-${task.id}`}
                          >
                            <User className="h-3 w-3" />
                            {task.lead_name || 'View Lead'}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                          
                          {/* Due Date */}
                          <div className={`flex items-center gap-1 text-sm ${
                            overdue ? 'text-red-600' : 'text-slate-500'
                          }`}>
                            <Calendar className="h-3 w-3" />
                            <span>Due {formatDueDate(task.due_date)}</span>
                            {overdue && (
                              <Badge variant="destructive" className="ml-1 text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>
                          
                          {/* Assigned User */}
                          {task.user_name && (
                            <span className="text-xs text-slate-400">
                              Assigned to {task.user_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setTaskToDelete(task);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
